import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { changePasswordSchema } from 'utils/validate';
import { FormChangePassword } from 'types/setting';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { changeInfo } from 'services/auth';
import { StorageServices } from 'services/storage';
import { useNavigate } from 'react-router-dom';
import { useFlash } from 'contexts/flash';
import { DeleteLogo, UploadLogo } from 'services/logo';
import { useSystemInfo } from 'contexts/systemInfo';
const storage = new StorageServices();

export const useInjection = () => {
  const { setSystemInfo, systemInfo } = useSystemInfo();
  const navigate = useNavigate();
  const fileInputIconRef = useRef<HTMLInputElement>(null);
  const { setFlash } = useFlash();
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState<boolean>(false);
  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<FormChangePassword>({ resolver: yupResolver(changePasswordSchema) });

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    const formData = new FormData();
    const blob = new Blob([fileList[0]], { type: fileList[0]?.type });
    formData.append('file', blob, fileList[0]?.name);
    const url = await UploadLogo(formData);
    setSystemInfo({ ...systemInfo, logoUrl: url });
  };
  const { asyncCallback: asyncUploadFile } = useAsyncCallback(handleFileChange, []);

  const onChangePassword = useCallback(
    async (value: FormChangePassword) => {
      await changeInfo({ newPassword: value.newPassword, currentPassword: value.currentPassword });
      setFlash({ type: 'success', message: 'change password successfully' });
      storage.removeAccessToken();
      storage.removeRefreshToken();
      navigate('/login');
    },
    [navigate, setFlash]
  );
  const handleFileClick = useCallback(() => {
    if (fileInputIconRef.current) {
      fileInputIconRef.current.click();
    }
  }, []);

  const handleDeleteLogo = useCallback(async () => {
    await DeleteLogo();
    setSystemInfo({ ...systemInfo, logoUrl: '' });
  }, [systemInfo, setSystemInfo]);

  const { asyncCallback: asyncChangePassword, isLoading } = useAsyncCallback(onChangePassword, []);

  return {
    showCurrentPassword,
    setShowCurrentPassword,
    showNewPassword,
    showConfirmNewPassword,
    setShowConfirmNewPassword,
    setShowNewPassword,
    handleSubmit,
    register,
    errors,
    asyncChangePassword,
    isLoading,
    fileInputIconRef,
    handleFileChange: asyncUploadFile,
    fileIcon: systemInfo?.logoUrl,
    handleFileClick,
    handleDeleteLogo
  };
};
